import { useEffect, useRef, useState } from 'react';

export interface UseHideOnScrollOptions {
  threshold?: number;
  targetSelector?: string;
  listenOnWindow?: boolean;
  autoDetect?: boolean; // scan for large scrollable elements and attach
  disabled?: boolean;
}

export default function useHideOnScroll(options: UseHideOnScrollOptions = {}) {
  const {
    threshold = 8,
    targetSelector,
    listenOnWindow = true,
    autoDetect = false,
    disabled = false,
  } = options;

  const [hidden, setHidden] = useState(false);
  const rafRef = useRef(false);
  const lastPos = useRef(new Map<any, number>());
  const listenersRef = useRef<Array<{ target: any; fn: EventListener }>>([]);

  const handleDelta = (target: any, current: number) => {
    const prev = lastPos.current.get(target) ?? current;
    const delta = current - prev;
    if (delta > threshold) setHidden(true);
    else if (delta < -threshold) setHidden(false);
    lastPos.current.set(target, current);
  };

  const addScrollTarget = (t: any) => {
    if (!t) return;
    const fn = () => {
      if (!rafRef.current) {
        rafRef.current = true;
        window.requestAnimationFrame(() => {
          try {
            const current = t === window ? (window.scrollY || window.pageYOffset || 0) : (t as Element).scrollTop || 0;
            handleDelta(t, current);
          } catch (e) {
            // ignore
          }
          rafRef.current = false;
        });
      }
    };

    try {
      if (t === window) window.addEventListener('scroll', fn, { passive: true });
      else (t as Element).addEventListener('scroll', fn, { passive: true });
      listenersRef.current.push({ target: t, fn });
      if (t === window) lastPos.current.set(t, window.scrollY || 0);
      else lastPos.current.set(t, (t as Element).scrollTop || 0);
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => {
    if (typeof window === 'undefined' || disabled) return undefined;

    const isTouch = !!(navigator && (navigator.maxTouchPoints > 0 || 'ontouchstart' in window));
    const isNarrow = window.matchMedia && window.matchMedia('(max-width: 900px)').matches;
    if (!isTouch && !isNarrow) {
      // Still attach wheel to detect mouse wheel on desktop when desired
    }

    // attach window if requested
    if (listenOnWindow) addScrollTarget(window);

    // explicit selector binding
    if (targetSelector) {
      try {
        const el = document.querySelector(targetSelector) as Element | null;
        if (el) addScrollTarget(el);
      } catch (e) {}
    }

    // auto-detect large scrollable containers
    if (autoDetect) {
      try {
        const nodes = Array.from(document.querySelectorAll('body *')) as Element[];
        const vh = window.innerHeight || document.documentElement.clientHeight || 768;
        for (const n of nodes) {
          const s = getComputedStyle(n);
          const overflowY = (s.overflowY || '').toLowerCase();
          const rect = n.getBoundingClientRect();
          const isScrollable = (overflowY === 'auto' || overflowY === 'scroll') && (n.scrollHeight > n.clientHeight + 4);
          const isLarge = rect.height >= Math.max(200, vh * 0.6);
          if (isScrollable && isLarge) {
            addScrollTarget(n);
            if (listenersRef.current.length > 8) break;
          }
        }
      } catch (e) {}
    }

    // pointer/touch/wheel detectors to catch transform-based gestures
    let lastPointerY: number | null = null;
    let lastTouchY: number | null = null;

    const onPointerDown = (ev: PointerEvent) => { lastPointerY = ev.clientY; };
    const onPointerMove = (ev: PointerEvent) => {
      if (lastPointerY == null) return;
      const y = ev.clientY; const delta = lastPointerY - y; lastPointerY = y;
      if (Math.abs(delta) < 6) return;
      if (delta > 0) setHidden(true); else setHidden(false);
    };
    const onPointerUp = () => { lastPointerY = null; };

    const onTouchStart = (ev: TouchEvent) => { const t = ev.touches && ev.touches[0]; if (t) lastTouchY = t.clientY; };
    const onTouchMove = (ev: TouchEvent) => { const t = ev.touches && ev.touches[0]; if (!t || lastTouchY == null) return; const y = t.clientY; const delta = lastTouchY - y; lastTouchY = y; if (Math.abs(delta) < 6) return; if (delta > 0) setHidden(true); else setHidden(false); };
    const onTouchEnd = () => { lastTouchY = null; };

    const onWheel = (ev: WheelEvent) => { const delta = ev.deltaY; if (Math.abs(delta) < 6) return; if (delta > 0) setHidden(true); else setHidden(false); };

    try {
      window.addEventListener('pointerdown', onPointerDown, { passive: true });
      window.addEventListener('pointermove', onPointerMove, { passive: true });
      window.addEventListener('pointerup', onPointerUp, { passive: true });
      window.addEventListener('touchstart', onTouchStart, { passive: true });
      window.addEventListener('touchmove', onTouchMove, { passive: true });
      window.addEventListener('touchend', onTouchEnd, { passive: true });
      window.addEventListener('wheel', onWheel, { passive: true });
      try { document.addEventListener('pointermove', onPointerMove, { passive: true }); } catch (e) {}
      try { document.documentElement.addEventListener('touchmove', onTouchMove, { passive: true }); } catch (e) {}
    } catch (e) {}

    return () => {
      // remove attached scroll listeners
      for (const l of listenersRef.current) {
        try {
          if (l.target === window) window.removeEventListener('scroll', l.fn as EventListener);
          else (l.target as Element).removeEventListener('scroll', l.fn as EventListener);
        } catch (e) {}
      }
      listenersRef.current = [];
      try {
        window.removeEventListener('pointerdown', onPointerDown as EventListener);
        window.removeEventListener('pointermove', onPointerMove as EventListener);
        window.removeEventListener('pointerup', onPointerUp as EventListener);
        window.removeEventListener('touchstart', onTouchStart as EventListener);
        window.removeEventListener('touchmove', onTouchMove as EventListener);
        window.removeEventListener('touchend', onTouchEnd as EventListener);
        window.removeEventListener('wheel', onWheel as EventListener);
        try { document.removeEventListener('pointermove', onPointerMove as EventListener); } catch (e) {}
        try { document.documentElement.removeEventListener('touchmove', onTouchMove as EventListener); } catch (e) {}
      } catch (e) {}
    };
  }, [threshold, targetSelector, listenOnWindow, autoDetect, disabled]);

  return { hidden, setHidden } as const;
}
